import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Register() {
  const { t } = useLang();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "teacher" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(form);
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "coach") navigate("/coach");
      else navigate("/onboarding");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.registerTitle")} subtitle={t("auth.registerSubtitle")}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-2">
          <Label htmlFor="name">{t("auth.name")}</Label>
          <Input id="name" required value={form.name} onChange={set("name")} className="h-12 rounded-xl" data-testid="register-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} className="h-12 rounded-xl" data-testid="register-email" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="h-12 rounded-xl" data-testid="register-password" />
        </div>
        <div className="space-y-2">
          <Label>{t("auth.role")}</Label>
          <Select value={form.role} onValueChange={set("role")}>
            <SelectTrigger className="h-12 rounded-xl" data-testid="register-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="teacher">{t("auth.roleTeacher")}</SelectItem>
              <SelectItem value="coach">{t("auth.roleCoach")}</SelectItem>
              <SelectItem value="admin">{t("auth.roleAdmin")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="register-submit">
          {loading ? t("common.loading") : t("auth.registerBtn")}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          {t("auth.haveAccount")} <Link to="/login" className="text-accent font-semibold hover:underline" data-testid="to-login">{t("auth.loginBtn")}</Link>
        </p>
      </form>
    </AuthShell>
  );
}
